import { Formik } from "formik";
import { useRef, useState } from "react";
import CMSFormTelephone from "../formFields/CMSFormTelephone";
import CMSFormInput from "../formFields/CMSFormInput";
import CMSFormTextArea from "../formFields/CMSFormTextArea";
import CMSFormFile from "../formFields/CMSFormFile";
import { jobSchema } from "../utils/validations";
import GeneralButton from "../common/GeneralButton";
import ContainerLoading from "../common/loading/ContainerLoading";
import { motion } from "framer-motion";
import success from "../../assets/images/success.png";
import { useToast } from "../contexts/ToastContext";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../config/axios";

const CareersApplyForm = ({ job }) => {
  const [status, setStatus] = useState("idle"); // "idle" | "loading" | "success" | "error"
  const [error, setError] = useState("");
  const { addToast } = useToast();
  const { id } = useParams();
  const navigate = useNavigate();

  const refs = {
    firstName: useRef(null),
    lastName: useRef(null),
    email: useRef(null),
    mobile: useRef(null),
    file: useRef(null),
    message: useRef(null),
    noticePeriod: useRef(null),
  };

  const scrollToFirstError = (errors) => {
    const firstErrorField = Object.keys(errors)[0];
    if (!firstErrorField || !refs[firstErrorField]?.current) return;

    const el = refs[firstErrorField].current;
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
    setTimeout(() => el.focus?.({ preventScroll: true }), 500);
  };

  const handleSubmit = async (values, { resetForm }) => {
    setStatus("loading");

    try {
      const formData = new FormData();
      for (let name in values) {
        if (Array.isArray(values[name])) {
          formData.append(name, JSON.stringify(values[name]));
        } else {
          formData.append(name, values[name]);
        }
      }
      await axios.post("/jobs", formData);

      setStatus("success");

      setTimeout(() => {
        resetForm();
        setStatus("idle");
        navigate(`/careers/${id}`);
      }, 10000);
    } catch (err) {
      console.error(err);
      setStatus("idle");
      if (err.status === 409) {
        setError(
          "You have already submitted an application for this position."
        );
        setTimeout(() => {
          setError("");
        }, 5000);
      } else {
        setError("Submission failed. Please try again.");
        setTimeout(() => {
          setError("");
        }, 5000);
      }
    }
  };

  return (
    <div className="mb-2 lg:mb-0  rounded-[15px] relative min-h-[300px]">
      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center rounded-[15px] z-10">
          <ContainerLoading />
        </div>
      )}

      {status === "success" && (
        <motion.div
          initial={{ x: -200, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -200, opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <div className="grid bg-secondary rounded-[15px] grid-cols-1 gap-8 items-start">
            <div className="w-full rounded-[15px] p-10 md:px-10 md:py-10 2xl:p-16 ">
              <div className="grid grid-cols-1 lg:grid-cols-[2.5fr_1fr] gap-2 lg:gap-10 ">
                <div className=" md:p-5 flex flex-col justify-between order-2 lg:order-1 ">
                  <p className="text-white text-28pt md:text-48pt leading-none mb-8 text-center lg:text-left">
                    Job Application Submitted Successfully
                  </p>
                  <p className="text-orange text-[25px] md:text-[34px] leading-none text-center lg:text-left ">
                    Thank you for applying. Your application has been received
                    and is under review. You will receive an email confirmation
                    shortly.
                  </p>
                </div>
                <div className="flex items-center justify-center order-1 lg:order-2 mb-8 xl:mb-0">
                  <img
                    src={success}
                    alt="success"
                    className="w-[80px] h-[80px] md:w-[110px] md:h-[110px] lg:w-[134px] lg:h-[134px]"
                  />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {status !== "success" && (
        <Formik
          initialValues={{
            jobId: job._id,
            firstName: "",
            lastName: "",
            email: "",
            mobile: "",
            noticePeriod: "",
            file: null,
            message: "",
          }}
          validationSchema={jobSchema}
          onSubmit={handleSubmit}
        >
          {(form) => (
            <div className="w-full grid xl:grid-cols-[2.5fr_1fr] gap-4 xl:gap-8 items-start">
              {/* Personal info fields */}
              <div className="grid grid-cols-1 bg-[#DEE2DD]/40 rounded-[15px] p-6 md:p-10  gap-8 order-2 xl:order-1">
                <p className="text-secondary text-24pt md:text-28pt leading-tight mb-0">
                  You're applying for the post of
                  <span className="text-orange"> " {job.title} " </span>at
                  Nutricub.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 ">
                  <CMSFormInput
                    name="firstName"
                    label="First Name"
                    form={form}
                    className="1"
                    ref={refs.firstName}
                    required
                  />
                  <CMSFormInput
                    name="lastName"
                    label="Last Name"
                    form={form}
                    className="1"
                    ref={refs.lastName}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <CMSFormInput
                    name="email"
                    label="Email"
                    form={form}
                    className="1"
                    ref={refs.email}
                    required
                  />
                  <CMSFormTelephone
                    name="mobile"
                    label="Phone"
                    form={form}
                    className="1"
                    ref={refs.mobile}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <CMSFormInput
                    name="noticePeriod"
                    label="Notice Period"
                    smallDescription=" (In days)"
                    form={form}
                    className="1"
                    ref={refs.noticePeriod}
                    required
                  />
                  <CMSFormFile
                    name="file"
                    label="Attach Resume"
                    form={form}
                    ref={refs.file}
                    required
                  />
                </div>

                <CMSFormTextArea
                  name="message"
                  label="Message"
                  form={form}
                  ref={refs.message}
                  maxLength={300}
                  required
                />
                {error && (
                  <div
                    className={`relative transition-all duration-300 ease-in-out ${
                      error
                        ? "opacity-100 -translate-y-2 md:translate-y-0"
                        : "opacity-0 -translate-y-2"
                    }`}
                  >
                    {error && <span className="errorMessage">{error}</span>}
                  </div>
                )}
                <div className="flex items-end justify-end">
                  <GeneralButton
                    text="Submit"
                    type="submit"
                    title="Click to submit"
                    className="text-lg px-4"
                    onClick={() => {
                      form.handleSubmit();
                      setTimeout(() => scrollToFirstError(form.errors), 100);
                    }}
                  />
                </div>
              </div>
              {/* Job info */}
              <div className="flex bg-secondary p-6 lg:px-8 gap-4 items-center rounded-[15px] relative order-1 xl:order-2">
                <div className="flex-1 relative rounded-md">
                  <div className="flex flex-col gap-1">
                    {job.title && (
                      <p className="title text-white text-28pt leading-tight">
                        {job.title}
                      </p>
                    )}
                    {job.shortDescription && (
                      <p className="subtitle text-20pt 1xl:text-24pt leading-none my-2 font-mono text-orange">
                        {job.shortDescription}
                      </p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-3 text-sm text-gray-500">
                      {job.employmentType && (
                        <span className="px-3 py-1 text-16pt xl:text-18pt border border-quaternary font-mono text-quaternary rounded-full">
                          {job.employmentType}
                        </span>
                      )}
                      {job.joiningTime && (
                        <span className="px-3 py-1 text-16pt xl:text-18pt border border-quaternary font-mono text-quaternary rounded-full">
                          Joining: {job.joiningTime}
                        </span>
                      )}
                      {job.salary && (
                        <span className="px-3 py-1 text-16pt xl:text-18pt border border-quaternary font-mono text-quaternary rounded-full">
                          Salary: {job.salary}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Formik>
      )}
    </div>
  );
};

export default CareersApplyForm;
